import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, ExternalLink, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${n.toFixed(2)}`;

export default function PaymentCard({ token, payment, lovedOne, onChange }) {
  const [saving, setSaving] = useState(false);

  const markSent = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/upload/${token}/payment-sent`);
      onChange(data);
      toast.success("Thank you. We'll confirm as soon as it arrives.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (payment.status !== "unpaid") {
    const confirmed = payment.status === "confirmed";
    return (
      <div data-testid="payment-status-card" className="mt-10 card-surface p-6 flex items-start gap-4">
        {confirmed ? <CheckCircle2 size={22} className="text-[#D4AF37] shrink-0 mt-0.5" strokeWidth={1.4} /> : <Clock size={22} className="text-[#D4AF37] shrink-0 mt-0.5" strokeWidth={1.4} />}
        <div>
          <div data-testid="payment-status-text" className="serif text-xl text-[#F5F0E8]">
            {confirmed ? "Payment received — thank you" : "Payment sent — we're confirming it"}
          </div>
          <p className="text-sm text-[#A0988E] mt-1">{money(payment.total)} via Cash App to {payment.cashtag}</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="payment-card" className="mt-10 card-surface p-6 sm:p-8">
      <div className="overline mb-3">Final step · Payment</div>
      <h2 className="serif text-3xl font-light text-[#F5F0E8]">Pay with Cash App</h2>

      <div className="mt-6 max-w-sm space-y-2 text-[15px]">
        <div className="flex justify-between text-[#F5F0E8]/90"><span>Memorial Tribute Film</span><span>{money(payment.base)}</span></div>
        <div className="flex justify-between text-[#A0988E]"><span>Cash App fee (3%)</span><span data-testid="payment-fee">{money(payment.fee)}</span></div>
        <div className="hair my-3" />
        <div className="flex justify-between text-[#F5F0E8] text-lg"><span className="serif">Total</span><span data-testid="payment-total" className="serif">{money(payment.total)}</span></div>
      </div>

      <p className="mt-6 text-sm text-[#A0988E] spectral leading-relaxed max-w-xl">
        Send <span className="text-[#F5F0E8]">{money(payment.total)}</span> to <span className="text-[#F5F0E8]">{payment.cashtag}</span>.
        Please write <span className="text-[#F5F0E8]">&ldquo;{lovedOne}&rdquo;</span> in the Cash App note so we can match your payment.
      </p>

      <div className="mt-7 flex flex-wrap items-center gap-4">
        <a data-testid="cashapp-pay-button" href={payment.url} target="_blank" rel="noopener noreferrer"
           className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-white bg-[#00D632] hover:bg-[#00c22d] transition-colors">
          Pay {money(payment.total)} on Cash App <ExternalLink size={15} />
        </a>
        <button data-testid="payment-sent-button" onClick={markSent} disabled={saving} className="btn-ghost disabled:opacity-60">
          I&apos;ve sent the payment
        </button>
      </div>
    </div>
  );
}
